<template>
    <div class="py-12">
        <div class="flex justify-between h-[45px]">
            <div
                class="group/domain -m-2 flex items-center gap-3 rounded-lg p-2 text-left font-medium transition-colors duration-150"
            >
                <div
                    v-if="domainState.active"
                    class="h-2 w-2 rounded-full bg-green-500"
                ></div>
                <div v-else class="h-2 w-2 rounded-full bg-gray-400"></div>
                <h1 class="text-2xl font-bold text-gray-800">
                    {{ reportedDomain.domain }}
                </h1>
            </div>
        </div>

        <CrumbsBarMinimal :domain="reportedDomain.domain" :source="null" />

        <BasicTabs :tabs="tabs" :tab-key="'domain-settings'" />

        <div class="mt-10 grid grid-cols-[260px_1fr] gap-12">
            <div class="flex flex-col">
                <div class="mb-55">
                    <h3 class="text-gray-800 font-semibold text-lg">
                        Domain Details
                    </h3>
                    <p class="text-gray-500 text-sm">
                        Basic info about your domain
                    </p>
                </div>

                <div class="mb-135">
                    <h3 class="text-gray-800 font-semibold text-lg">
                        Domain Details
                    </h3>
                    <p class="text-gray-500 text-sm">Update your TXT record</p>
                </div>

                <div>
                    <h3 class="text-gray-800 font-semibold text-lg">
                        Remove Domain
                    </h3>
                    <p class="text-gray-500 text-sm">
                        You can delete your domain and all existing reports
                    </p>
                </div>
            </div>

            <div class="space-y-12">
                <div class="bg-white shadow rounded-xl p-6">
                    <div class="flex items-start justify-left gap-70 pb-4">
                        <div>
                            <div class="text-gray-800 font-semibold pl-2">
                                Status
                            </div>

                            <div
                                class="mt-1 inline-flex items-center justify-center gap-1 rounded-md px-2.5 py-1 text-[12px] font-semibold whitespace-nowrap border"
                                :class="
                                    domainState.active
                                        ? 'border-[#c8ead7] bg-[#f2fdf7] text-[#0f7c3c]'
                                        : 'border-[#d5d8de] bg-[#f5f6f8] text-[#6a7280]'
                                "
                            >
                                {{ domainState.active ? "Active" : "Inactive" }}
                            </div>
                        </div>

                        <div>
                            <div class="text-gray-800 font-semibold">
                                Created
                            </div>
                            <div class="text-gray-500 text-sm">
                                {{ createdAtFormatted }}
                            </div>
                        </div>

                        <div>
                            <div class="text-gray-800 font-semibold">
                                DMARC Policy
                            </div>

                            <div class="text-gray-500 text-sm mt-1">
                                <span v-if="reportedDomain.dmarcRecord">
                                    <span
                                        v-if="
                                            domainState.dmarcPolicy === 'reject'
                                        "
                                        class="body-td-reject"
                                        >reject</span
                                    >
                                    <span
                                        v-else-if="
                                            domainState.dmarcPolicy ===
                                            'quarantine'
                                        "
                                        class="body-td-quarantine"
                                        >quarantine</span
                                    >
                                    <span v-else class="body-td-none"
                                        >none</span
                                    >
                                </span>
                                <span v-else class="body-td-none">none</span>
                            </div>
                        </div>
                    </div>

                    <div class="w-full h-px bg-gray-200 my-6"></div>
                    <div class="flex justify-end">
                        <button
                            class="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md transition"
                            @click="checkDnsRecords"
                        >
                            Reload Record
                        </button>
                    </div>
                </div>

                <div class="bg-white shadow rounded-xl p-8">
                    <h3 class="text-gray-800 font-semibold text-lg mb-4">
                        Configure your TXT Record
                    </h3>

                    <ol
                        class="list-decimal list-inside text-gray-700 text-sm space-y-1 mb-8"
                    >
                        <li>
                            To begin setting up DMARC at your DNS provider, log
                            in to your DNS Hosting provider's website and
                            navigate to the section for creating records.
                        </li>
                        <li>
                            Look for the option to create/update a new record or
                            access the TXT section for editing.
                        </li>
                        <li>Select the TXT DNS record type.</li>
                        <li>
                            When adding the host value, use
                            <strong>_dmarc</strong> for the main domain.
                        </li>
                        <li>
                            Enter the following text information into the
                            "Value" field:
                        </li>
                        <li>
                            Values will be updated based on your selected
                            options below.
                        </li>
                    </ol>

                    <div class="grid grid-cols-3 gap-4 mb-6">
                        <div>
                            <label class="text-gray-700 font-medium block mb-1"
                                >DMARC Policy</label
                            >
                            <SelectInput
                                v-model="policy"
                                :data="policyOptions"
                                placeholder="Select policy"
                            />
                        </div>
                    </div>

                    <h4 class="font-semibold text-gray-800 mb-2">
                        Your TXT Record
                    </h4>

                    <textarea
                        class="w-full rounded-lg border p-3 text-sm bg-gray-50"
                        rows="3"
                        readonly
                        :value="txtRecord"
                    ></textarea>

                    <button
                        class="mt-4 text-sm border rounded-md px-3 py-2 hover:bg-gray-100 transition"
                        @click="copyRecord"
                    >
                        Copy to clipboard
                    </button>
                </div>

                <!-- REMOVE DOMAIN CARD -->
                <div class="bg-white shadow rounded-xl p-8">
                    <h3 class="font-semibold text-gray-800 mb-1">
                        Your Domain
                    </h3>
                    <p class="text-gray-500 text-sm mb-6">
                        {{ reportedDomain.domain }}
                        <span class="italic">(This step can’t be undone.)</span>
                    </p>

                    <button
                        class="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md"
                        @click="deleteDomain"
                    >
                        Delete Domain
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import route from "ziggy-js";
import type { PropType } from "vue";
import axios from "axios";
import BasicTabs from "@/components/Atomic/Tabs/BasicTabs.vue";
import DateRangeField from "@/Pages/Reports/components/DateRangeField.vue";
import { Globe, Settings } from "lucide-vue-next";

interface ReportedDomain {
    domain: string;
    active: boolean;
    created_at?: string;
    dmarcPolicy: string;
}

const props = defineProps({
    reportedDomain: {
        type: Object as PropType<ReportedDomain>,
        required: true,
    },
});

const domainState = reactive({
    active: false,
    dmarcPolicy: "none",
    dmarcRecord: null as string | null,
    created_at: null as string | null,
});

onMounted(() => {
    domainState.active = props.reportedDomain.active;
    domainState.dmarcPolicy = props.reportedDomain.dmarcPolicy;
    domainState.dmarcRecord = props.reportedDomain.dmarcRecord ?? null;
    domainState.created_at = props.reportedDomain.created_at ?? null;
});

/* ------------------ STATUS ------------------ */
const createdAtFormatted = computed(() => {
    if (!props.reportedDomain.created_at) return "-";
    return new Date(props.reportedDomain.created_at).toLocaleString();
});

/* ------------------ TXT COPY RECORD ------------------ */
const txtRecord = computed(
    () =>
        `v=DMARC1; p=${policy.value}; rua=mailto:${props.reportedDomain.account}@rua.dmarceye.com; ri=86400`,
);

const copyRecord = () => {
    const el = document.createElement("textarea");
    el.value = txtRecord.value;
    document.body.appendChild(el);
    el.select();
    document.execCommand("copy");
    document.body.removeChild(el);
};

/* ------------------ TXT RECORD ------------------ */
const policy = ref(props.reportedDomain.dmarcPolicy?.toString() || "none");

const policyOptions = [
    { title: "None", value: "none" },
    { title: "Quarantine", value: "quarantine" },
    { title: "Reject", value: "reject" },
];

const dnsCheckingInProgress = ref(false);
const dnsResult = ref<null | boolean>(null);

/* ------------------ RELOAD DNS ------------------ */

const checkDnsRecords = async () => {
    if (dnsCheckingInProgress.value) return;

    dnsCheckingInProgress.value = true;
    dnsResult.value = null;

    try {
        const response = await axios.get(
            route("domains.check-dns", { domain: props.reportedDomain.domain }),
        );

        if (response.data.success) {
            const result = response.data.result;

            domainState.active = result.active;
            domainState.dmarcPolicy = result.dmarc_policy;
            domainState.dmarcRecord = result.dmarc_record;

            dnsResult.value = result;
        } else {
            console.warn("DNS checking failed:", response.data.error);
            dnsResult.value = null;
        }
    } catch (error) {
        console.error("Failed to check DNS records:", error);
        dnsResult.value = null;
    } finally {
        dnsCheckingInProgress.value = false;
    }
};

/* ------------------ DELETE DOMAIN ------------------ */
const deleteDomain = async () => {
    await axios.delete(
        route("domains.destroy", { domain: props.reportedDomain.domain }),
    );

    window.location.href = route("domains.index");
};

/* ------------------ TAB SWITCHER ------------------ */
const tabs: TabItem[] = [
    {
        title: "Reports",
        key: "domain-reports",
        icon: Globe,
        url: route("reports.show", { domain: props.reportedDomain.domain }),
    },
    {
        title: "Settings",
        key: "domain-settings",
        icon: Settings,
        url: route("reports.settings.show", {
            domain: props.reportedDomain.domain,
        }),
    },
];
</script>
